import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { sentryCaptureException } from 'sentry.js';
import * as clientErrTypes from 'config/clientErrorTypes';
import tokenManager from 'client/utils/tokenManager';
import {
    isFetchNetworkError,
    isOneOfErrors,
    isUnauthorizedToGetAccessToken,
    isFeverApiAccessTokenExpired,
    isGetWinnerDuplicateError,
    isGetWinnerEmptySuspendError,
    isGetWinnerWhenKKboxOrderUnfulfillErr,
    isErrorWhenPromoSuspend,
    isEntryPromoUuidMismatchError,
    isFormApiAccessTokenExpired,
    isFormApiDuplicateSubmitFormError,
    isFormApiSetGameResultWhenSuspendErr,
    isSetGameResultWhenContestModuleRecalculatingRank,
    isTooManyAttemptErr,
    isFailedToLoadError,
    isFailedToFetchError,
    isErrorWhenPromoEnd,
    isErrOfGetEntryWhenPromoInvalid,
    isLineLoginCredentialErr,
    isErrPromoSubmitRecaptchaExpired,
    isRequestAtInvalidPromoTime,
    isErrCreateEntriesTimeOut,
    getGeneralErrCode,
    feverErrCodes,
    isErrOfGetEntryWhenReCaptchaMissing,
    isErrOfRecaptchaInvalidInput,
    isCreateEntryTotalMonthlyEntryLimitError,
    isErrOfSequentialTaskNotUnlocked,
    isUTagGoTokenError,
    isUTagGoExceptionError,
    normalizeApiErrInfo
} from 'client/utils/errorObjectChecker';
import { isIntentionalRedirecting } from 'client/utils/navigationIntent';
import { getTestQueryString } from 'api/client';
import { suspendPromo, endPromo } from 'client/redux/modules/promoState';
import {
    showGeneralErrorDialog,
    showPromoNotAvailableErrDialog,
    showDuplicateGetWinnerErrDialog,
    showPromoEndErrDialog,
    showGetPromoDataErrorDialog,
    showUnfufillKKboxOrderDialog,
    showNetworkErrorDialog,
    showUnauthorizedError,
    showContestRecalculatingCacheDialog,
    showTooManyAttemptErrDialog,
    showInvalidTestCodeErrorDialog,
    showRetryRecaptchaDialog,
    showFormSubmitDuplicateErrDialog,
    showGetPromoDataErrorAndNoRefreshBtnDialog,
    showRecaptchaMissingDialog,
    showMergeFrameFailedDialog,
    showWinnerNotFoundDialog,
    showPromoNotFoundDialog,
    showLiffAuthFailDialog,
    showFillFormDuplicateDialog,
    showGetCheckPointCardUnexpectedDialog,
    showOpenTaskUnexpectedDialog,
    showEntryFlowExistsDialog,
    showPointIsLockedErrorDialog,
    showThirdPartyApiTimedOutDialog,
    showTokenErrorDialog,
    showAIFailedWithPromoEndErrDialog,
    showEntryReachTotalLimitDialog,
    showStoreGameResultEntryTotalLimitDialog,
    showOptionNotFoundDialog,
    showPromoUnavailableCloseDialog,
    showBotProtectionFailedDialog,
    showSequentialTaskNotUnlockedDialog,
    showSystemErrorRefreshDialog
} from 'client/redux/modules/systemErrorDialog';

export default function useClientErrorHandler() {
    const dispatch = useDispatch();

    return useCallback(
        ({ err = {} } = {}) => {
            errorActionHandler(err, { dispatch });
        },
        [dispatch]
    );
}

export function errorActionHandler(errObj, { dispatch }) {
    console.error(errObj);
    // 刻意整頁導轉（如 OCEP needBind 導向綁定頁）會 abort 進行中的請求，丟出
    // Load failed / Failed to fetch / AbortError。此為離頁造成的中斷而非真的異常，
    // 不應在即將卸載的頁面上閃出錯誤彈窗。
    if (
        isIntentionalRedirecting() &&
        isOneOfErrors([isFailedToLoadError, isFailedToFetchError, isFetchNetworkError], errObj)
    ) {
        return;
    }
    const { code: generalErrCode, isShouldSendSentry, error } = getGeneralErrCode(errObj);
    if (generalErrCode) {
        let eventId = null;
        if (isShouldSendSentry) eventId = sentryCaptureException(error);

        return dispatch(showGetPromoDataErrorDialog(generalErrCode, eventId));
    }

    if (errObj.extraInfo) console.error(errObj.extraInfo);

    const { errSerialNo, errType, errMsgFromLoginServer, isGetAccessTokenFailed } =
        errObj.extraInfo ?? {};
    switch (true) {
        case errType === clientErrTypes.GET_API_ERROR_AND_NO_REFRESH_BTN:
            return dispatch(showGetPromoDataErrorAndNoRefreshBtnDialog());
        case errType === clientErrTypes.GET_API_ERROR:
            return dispatch(showGetPromoDataErrorDialog());
        case errType === clientErrTypes.PROMO_NOT_FOUND:
            return dispatch(showPromoNotFoundDialog());
        case errType === clientErrTypes.INVALID_TEST_CODE:
            return dispatch(showInvalidTestCodeErrorDialog());
        // 兩個 sponsor 層頁面（獎品中心、載具授權管理）共用這一條：登入伺服器有回
        // error_description 時直接顯示它，比通用錯誤好追
        case (errType === clientErrTypes.LOGIN_GET_ACCESS_TOKEN_FAILED_REWARD_INIT ||
            errType === clientErrTypes.LOGIN_GET_ACCESS_TOKEN_FAILED_CARRIER_AUTHORIZATION_INIT) &&
            errMsgFromLoginServer !== undefined:
            return dispatch(
                showGeneralErrorDialog({
                    code: errMsgFromLoginServer
                })
            );
        case isErrCreateEntriesTimeOut(errObj):
            return dispatch(
                showGeneralErrorDialog({
                    code: feverErrCodes.ERR_CREATE_ENTRIES_TIMEOUT
                })
            );
        case isFetchNetworkError(errObj):
            return dispatch(showNetworkErrorDialog());
        case isTooManyAttemptErr(errObj):
            return dispatch(showTooManyAttemptErrDialog());
        case isGetWinnerDuplicateError(errObj):
            return dispatch(
                showDuplicateGetWinnerErrDialog({
                    serial: errSerialNo,
                    code: feverErrCodes.ERR_DUPLICATE_GET_WINNER
                })
            );
        case isRequestAtInvalidPromoTime(errObj):
            return dispatch(showPromoNotAvailableErrDialog());
        // promo 與 entry 分屬不同活動＝client 把獎池與 entry 配錯（#86eyfdna9）。
        // 必須排在下面的 suspend 群組之前：它們共用 pm-v0-f0 這組碼，落進那條分支會
        // 顯示「活動未開放」並把 gameRunningStatus 標成 suspend，把 client bug
        // 偽裝成活動被暫停。這裡改成上報 Sentry + 一般系統錯誤，讓它查得到。
        case isEntryPromoUuidMismatchError(errObj): {
            const eventId = sentryCaptureException(errObj);
            return dispatch(
                showGeneralErrorDialog({
                    code: eventId,
                    serial: errSerialNo
                })
            );
        }
        case errType === clientErrTypes.PICK_PRIZE_LOTTERY_NO_REMAINDER:
        case isOneOfErrors(
            [
                isGetWinnerEmptySuspendError,
                isErrorWhenPromoSuspend,
                isFormApiSetGameResultWhenSuspendErr,
                isErrOfGetEntryWhenPromoInvalid
            ],
            errObj
        ):
            dispatch(suspendPromo());
            return dispatch(showPromoNotAvailableErrDialog());
        case isErrorWhenPromoEnd(errObj):
            dispatch(endPromo());
            return dispatch(showPromoEndErrDialog());
        case isGetAccessTokenFailed:
        case isOneOfErrors(
            [
                isUnauthorizedToGetAccessToken,
                isFeverApiAccessTokenExpired,
                isFormApiAccessTokenExpired
            ],
            errObj
        ):
            return dispatch(showUnauthorizedError());
        case isFormApiDuplicateSubmitFormError(errObj):
            return dispatch(showFormSubmitDuplicateErrDialog());
        case isGetWinnerWhenKKboxOrderUnfulfillErr(errObj):
            return dispatch(showUnfufillKKboxOrderDialog(errSerialNo));
        case isSetGameResultWhenContestModuleRecalculatingRank(errObj):
            return dispatch(showContestRecalculatingCacheDialog());
        case errType === clientErrTypes.CREATE_ENTRIES_TURNSTILE_VERIFICATION_FAILED:
            return dispatch(showBotProtectionFailedDialog());
        case isErrPromoSubmitRecaptchaExpired(errObj):
            return dispatch(showRetryRecaptchaDialog());
        case isLineLoginCredentialErr(errObj):
            return tokenManager.logout().then(() => window.location.reload());
        case isErrOfGetEntryWhenReCaptchaMissing(errObj):
            return dispatch(showRecaptchaMissingDialog());
        case isErrOfRecaptchaInvalidInput(errObj):
            sentryCaptureException(errObj);
            dispatch(suspendPromo());
            return dispatch(showPromoNotAvailableErrDialog());
        case isCreateEntryTotalMonthlyEntryLimitError(errObj):
            return dispatch(showPromoUnavailableCloseDialog());
        case isErrOfSequentialTaskNotUnlocked(errObj): {
            const { data } = normalizeApiErrInfo(errObj);
            return dispatch(
                showSequentialTaskNotUnlockedDialog(data?.qualificationMasterPromotionUuid)
            );
        }
        // uTagGo 其餘錯誤（非餘額不足的業務拒絕 / 第三方 HTTP 失敗 / 參數驗證失敗）
        // 統一顯示系統錯誤彈窗、送 Sentry 追蹤；餘額不足 (A6101) 與 token 失效另有專屬處理
        // 顯示碼優先用對方原因碼 (providerCode)，無則退回本系統 ResponseCode
        case isUTagGoExceptionError(errObj): {
            const rawPayload = errObj.extraInfo?.payload ?? errObj.payload ?? {};
            if (rawPayload.data?.providerCode) {
                return dispatch(showSystemErrorRefreshDialog(rawPayload.data?.providerCode));
            }
            const eventId = sentryCaptureException(errObj);
            return dispatch(
                showGeneralErrorDialog({
                    code: eventId,
                    serial: errSerialNo
                })
            );
        }
        case errType === clientErrTypes.PHOTO_MERGE_FAILED:
            sentryCaptureException(errObj);
            return dispatch(showMergeFrameFailedDialog());
        case errType === clientErrTypes.WINNER_NOT_FOUND:
            return dispatch(showWinnerNotFoundDialog());
        case errType === clientErrTypes.LIFF_INIT_AUTH_FAILED:
            return dispatch(showLiffAuthFailDialog());
        case errType === clientErrTypes.FILL_FORM_DUPLICATE:
            return dispatch(showFillFormDuplicateDialog());
        case errType === clientErrTypes.CHECK_POINT_CARD_FAILED:
            return dispatch(showGetCheckPointCardUnexpectedDialog(errSerialNo));
        case errType === clientErrTypes.TASK_AND_RELATED_PROMO_DISCONNECT:
            return dispatch(showOpenTaskUnexpectedDialog(errSerialNo));
        case errType === clientErrTypes.THIRD_PARTY_API_TIMED_OUT:
            return dispatch(showThirdPartyApiTimedOutDialog());
        case isUTagGoTokenError(errObj):
        case errType === clientErrTypes.TOKEN_ERROR:
            return dispatch(showTokenErrorDialog());
        case errType === clientErrTypes.UGC_GET_MEDIA_COUNTS:
        case errType === clientErrTypes.GET_KKBOX_USER_POINT:
        case errType === clientErrTypes.REDUCE_KKBOX_POINT:
        case errType === clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.REFERRAL_ENTRY_NOT_FOUND:
        case errType === clientErrTypes.MGM_LOTTERY_VALIDATION_FAILED.EXCEPTION:
        case errType === clientErrTypes.LOGIN_USER_INFO_FAILED:
        case errType === clientErrTypes.PRIZE_OPEN_WINNER:
        case errType === clientErrTypes.NOTIFY_CREATE_FAILED:
        case errType === clientErrTypes.NOTIFY_MISSING_REQUIRED_FIELDS:
        case errType === clientErrTypes.AI_FAILED_WITH_RETRY_AI_JOBS:
        case errType === clientErrTypes.MISSION_REWARD_DATE_FORMAT_ERROR:
        case errType === clientErrTypes.AI_WEB_GAME_IFRAME_LOAD_ERROR:
        case errType === clientErrTypes.AI_WEB_GAME_RUNTIME_ERROR:
        case errType === clientErrTypes.AI_WEB_GAME_START_API_ERROR:
        case errType === clientErrTypes.AI_WEB_GAME_VALIDATION_ERROR:
        case errType === clientErrTypes.ORDER_REWARD_ENTRY_NOT_FOUND:
        case errType === clientErrTypes.STORE_ENTRY_SHARE_RECORD_FAILED:
            // STORE_ENTRY_SHARE_RECORD_FAILED 是 best-effort 寫入：失敗只影響 LINE 卡片比例
            // （退回預設 1.52:1），不該中斷分享流程。entry 被軟刪（ENT-R1-FNF1）走這裡靜默處理。
            sentryCaptureException(errObj);
            break;
        case errType === clientErrTypes.AI_FAILED_WITH_PROMO_END:
            return dispatch(showAIFailedWithPromoEndErrDialog());
        case errType === clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK:
        case errType === clientErrTypes.POINTS_IS_NOT_ENOUGH:
            return dispatch(showPromoNotAvailableErrDialog());
        case errType === clientErrTypes.STORE_GAME_RESULT_ENTRY_FLOW_EXISTS:
            return dispatch(showEntryFlowExistsDialog());
        case errType === clientErrTypes.STORE_GAME_RESULT_ENTRY_TOTAL_LIMIT:
            return dispatch(
                showStoreGameResultEntryTotalLimitDialog({
                    isTestMode: Boolean(getTestQueryString())
                })
            );
        case errType === clientErrTypes.POINT_IS_LOCKED:
            sentryCaptureException(errObj);
            return dispatch(showPointIsLockedErrorDialog());
        case errType === clientErrTypes.ENTRY_REACH_TOTAL_LIMIT:
            return dispatch(showEntryReachTotalLimitDialog());
        case errType === clientErrTypes.FORM_DATA_WINNER_NOT_FOUND:
        case errType === clientErrTypes.SURVEY_RESULT_PAGE_FAILED:
        case errType === clientErrTypes.NOTIFY_CREATE_FAILED_BUT_NO_SENTRY:
            return;
        case errType === clientErrTypes.OPTION_NOT_FOUND:
            return dispatch(showOptionNotFoundDialog());
        default: {
            const eventId = sentryCaptureException(errObj);
            dispatch(
                showGeneralErrorDialog({
                    code: eventId,
                    serial: errSerialNo
                })
            );
        }
    }
}

/**
 * 若指定將 body 送往 sentry，需將以下機敏性資料做轉換
 * @property {string} carrierCode - 載具密碼
 * @property {string} carrierNumber - 手機載具
 */
const secretKey = ['carrierCode', 'carrierNumber'];
const convertSecretKey = body => {
    const res = {};
    Object.entries(body).forEach(([key, value]) => {
        res[key] = secretKey.includes(key) ? '******' : value;
    });
    return res;
};

export function generateApiErrObj(apiErr = {}, type, body) {
    // 只有當 apiErr 是 Error 且已經有 isActivelyCatchErr 時，才保留原 Error
    // 否則建立新 Error（保持原本行為，避免影響一般 catch 區塊）
    const shouldPreserveError = apiErr instanceof Error && apiErr.isActivelyCatchErr === true;
    let errObj = shouldPreserveError ? apiErr : new Error(type);

    // 如果建立新的 Error，設定訊息
    if (!shouldPreserveError) {
        errObj.message = type;
    }

    // 建立 extraInfo
    // 對於 Error 物件，展開其屬性避免循環引用
    // 對於其他類型（包含陣列和普通物件），直接賦值（與原本實作一致）
    if (apiErr instanceof Error) {
        // 如果是 Error 物件，保留原本的 extraInfo（如果有的話），並合併新的 errType
        const extraInfo = apiErr.extraInfo
            ? { ...apiErr.extraInfo, errType: type }
            : {
                  message: apiErr.message,
                  stack: apiErr.stack,
                  name: apiErr.name,
                  errType: type
              };
        errObj.extraInfo = extraInfo;
    } else {
        // 對於非 Error（包含陣列和普通物件），直接賦值（與原本實作一致）
        apiErr.errType = type;
        errObj.extraInfo = apiErr;
    }

    // 保留原本的 isActivelyCatchErr 屬性（如果有的話）
    if (apiErr.isActivelyCatchErr !== undefined) {
        errObj.isActivelyCatchErr = apiErr.isActivelyCatchErr;
    }

    if (!Array.isArray(apiErr) && typeof apiErr === 'object' && Boolean(body)) {
        errObj.extraInfo.body = convertSecretKey(body);
    }

    return errObj;
}
